/**
 * Personalakte-Tab „Stellen": Zuordnungs-Grid (employee_positions) mit
 * Taetigkeitsart und Zulagenfeldern; Positions-Select aus api.organization.positions.
 */

import { useQuery } from "@tanstack/react-query"
import type { FieldDef, FormValues } from "@/components/admin/EntityFormSheet"
import type { Column } from "@/components/admin/MasterDataSection"
import { Badge } from "@/components/ui/badge"
import { api } from "@/services/api"
import type {
  EmployeePositionCreate,
  EmployeePositionEntry,
  TaetigkeitArt,
} from "@/types"
import { TAETIGKEIT_LABEL, toOptions } from "./labels"
import { SubTableEditor } from "./SubTableEditor"

const fmtRate = (v: EmployeePositionEntry["substituteSurchargeRate"]): string =>
  v === null || v === undefined || v === "" ? "—" : `${String(v).replace(".", ",")} €/h`

const COLUMNS: Column<EmployeePositionEntry>[] = [
  {
    header: "Stelle",
    cell: (r) => <span className="font-medium">{r.positionName ?? `#${r.positionId}`}</span>,
  },
  {
    header: "Tätigkeitsart",
    cell: (r) =>
      r.activityType ? (
        <Badge variant="secondary">{TAETIGKEIT_LABEL[r.activityType]}</Badge>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    header: "Vertretungszulage",
    className: "text-right tabular-nums",
    cell: (r) => fmtRate(r.substituteSurchargeRate),
  },
  {
    header: "Nachtschichtzulage",
    className: "text-right tabular-nums",
    cell: (r) => fmtRate(r.nightShiftRate),
  },
]

const str = (values: FormValues, key: string): string =>
  typeof values[key] === "string" ? (values[key] as string).trim() : ""

export function PositionsTab({ employeeId, canWrite }: { employeeId: string; canWrite: boolean }) {
  const positions = useQuery({
    queryKey: ["positions"],
    queryFn: () => api.organization.positions.list({ size: 100 }),
  })

  const fields: FieldDef[] = [
    {
      key: "positionId",
      label: "Stelle",
      kind: "select",
      required: true,
      options: (positions.data?.data ?? []).map((p) => ({ value: p.id, label: p.name })),
    },
    {
      key: "activityType",
      label: "Tätigkeitsart",
      kind: "select",
      emptyOption: "— keine Angabe —",
      options: toOptions(TAETIGKEIT_LABEL),
    },
    {
      key: "substituteSurchargeRate",
      label: "Vertretungszulage (€/h)",
      kind: "number",
      step: "0.01",
    },
    { key: "nightShiftRate", label: "Nachtschichtzulage (€/h)", kind: "number", step: "0.01" },
  ]

  return (
    <SubTableEditor<EmployeePositionEntry, EmployeePositionCreate>
      employeeId={employeeId}
      queryKey="positions"
      crud={api.employees.positions}
      columns={COLUMNS}
      fields={fields}
      entityLabel="Stellenzuordnung"
      createLabel="Stelle zuordnen"
      emptyText="Keine Stellen zugeordnet."
      canWrite={canWrite}
      toValues={(row) => ({
        positionId: row?.positionId ?? "",
        activityType: row?.activityType ?? "",
        substituteSurchargeRate:
          row?.substituteSurchargeRate !== null && row?.substituteSurchargeRate !== undefined
            ? String(row.substituteSurchargeRate)
            : "",
        nightShiftRate:
          row?.nightShiftRate !== null && row?.nightShiftRate !== undefined
            ? String(row.nightShiftRate)
            : "",
      })}
      fromValues={(values) => ({
        positionId: Number(str(values, "positionId")),
        activityType: (str(values, "activityType") as TaetigkeitArt) || null,
        substituteSurchargeRate: str(values, "substituteSurchargeRate") || null,
        nightShiftRate: str(values, "nightShiftRate") || null,
      })}
    />
  )
}
